import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, ScrollText, Radar } from "lucide-react";
import { API } from "../../context/AuthContext";

const display = "font-display";

export default function Insights({ eventCount, caseId }) {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!caseId) return;
    setInsight(null);
    axios.get(`${API}/insights/latest`, { params: { case_id: caseId } }).then((res) => {
      if (res.data && res.data.narrative) setInsight(res.data);
    }).catch(() => {});
  }, [caseId]);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/insights`, { case_id: caseId });
      setInsight(res.data);
      toast.success("Professor Flux finished skimming your timeline!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Insight generation misfired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#131f3a] border border-white/5 p-6 rounded-2xl shadow-xl flex flex-col min-h-[560px]" data-testid="insights-panel">
      <div className="border-b border-white/5 pb-4 mb-5 flex flex-wrap justify-between items-center gap-3">
        <div>
          <h3 className={`${display} font-bold text-base text-white uppercase tracking-wide`}>Flux Insight Engine</h3>
          <p className="text-xs text-gray-300 mt-0.5">Flux skims your entire timeline and writes the story — plus the patterns you might have missed.</p>
        </div>
        <button
          onClick={generate}
          disabled={loading || eventCount < 2}
          data-testid="generate-insights-btn"
          className={`bg-[#2dd4bf] hover:bg-[#5eead4] disabled:opacity-50 disabled:cursor-not-allowed text-gray-950 ${display} font-bold py-2.5 px-4 rounded-xl text-[11px] uppercase tracking-wider transition-colors duration-200 active:scale-[0.98] flex items-center gap-2`}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {loading ? "Flux Is Skimming..." : insight ? "Re-Analyze Timeline" : "Generate Insights"}
        </button>
      </div>

      {eventCount < 2 && !insight ? (
        <div className="text-gray-400 text-xs italic py-12 text-center">Flux needs at least 2 timeline events before he can analyze. Ingest some records first!</div>
      ) : !insight && !loading ? (
        <div className="text-gray-400 text-xs italic py-12 text-center" data-testid="insights-empty-state">
          Hit "Generate Insights" and Professor Flux will read every event in your matrix.
        </div>
      ) : loading && !insight ? (
        <div className="text-xs text-[#2dd4bf] font-mono animate-blink py-12 text-center">Professor Flux is reading your entire timeline, cover to cover...</div>
      ) : insight ? (
        <div className="space-y-6 overflow-y-auto">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <ScrollText className="w-4 h-4 text-[#f59e0b]" />
              <h4 className={`${display} text-xs font-bold uppercase tracking-wider text-[#f59e0b]`}>The Narrative</h4>
              <span className="text-[9px] font-mono text-gray-400">{insight.event_count} events analyzed · {new Date(insight.created_at).toLocaleString()}</span>
            </div>
            <div className="bg-gray-950 border border-white/5 rounded-xl p-5 space-y-3" data-testid="insight-narrative">
              {insight.narrative.split(/\n\n+/).map((para, i) => (
                <p key={i} className="text-[13px] text-gray-300 leading-relaxed">{para}</p>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <Radar className="w-4 h-4 text-[#2dd4bf]" />
              <h4 className={`${display} text-xs font-bold uppercase tracking-wider text-[#2dd4bf]`}>Pattern Insights</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="insight-patterns">
              {insight.patterns.map((p, i) => (
                <div key={p.title || i} className="bg-[#2dd4bf]/[0.04] border border-[#2dd4bf]/15 rounded-xl p-4">
                  <h5 className={`${display} text-xs font-bold text-white mb-1`}>{p.title}</h5>
                  <p className="text-[11px] text-gray-300 leading-relaxed">{p.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
